import shortid from "shortid";
import { Utils, Period } from "revlock-webutils";
import RevenueArrangementUtils from "./RevenueArrangementUtils";
import { ModificationType, RecognitionRule } from "../Reference";

/**
 * Class to initialize and prepare Revenue Arrangements for a Sales Order.
 * @class RevenueArrangementInitializer
 * @constructor
 * @param {Object} salesOrder - The Sales Order object to initialize.
 * @param {Object} context - The context containing configuration and organizational data.
 * @method prepareRevenueArrangements - Main method to prepare Revenue Arrangements.
 * @method removeArrangementsForWhReversal - Method to remove arrangements for warehouse reversals.
 * @method createNewRevenueArrangement - Method to create a new Revenue Arrangement if needed.
 * @method removeOpenPeriodRevenueArrangement - Method to remove Revenue Arrangements in open periods.
 * @method setUpNewRevenueArrangement - Method to set up the new Revenue Arrangement with items.
 * @returns {void}
 * @throws {Error} Throws an error if initialization fails.
 * @example
 * const initializer = new RevenueArrangementInitializer(salesOrder, context);
 * initializer.prepareRevenueArrangements();
 */
export default class RevenueArrangementInitializer {
    constructor(salesOrder, context) {
        this.salesOrder = salesOrder;
        this.context = context;
        this.configProvider = context.configProvider;
        this.organization = context.organization;
        this.calendarConfig = context.calendarConfig;
        this.recognizeByTransactionDate = context.recognizeByTransactionDate;
        this.errors = context.errors;
        this.orgConfig = context.orgConfig;
        this.orgConfigByKey = context.orgConfigByKey;
    }

    prepareRevenueArrangements() {
        // Step 1: Remove arrangements for warehouse reversal if applicable
        this.removeArrangementsForWhReversal();

        // Step 2: Initialize revenue arrangement related properties
        this.init();

        // Step 3: Remove revenue arrangements in open periods
        this.removeOpenPeriodRevenueArrangement();

        // Step 4: Create new revenue arrangement if needed
        this.createNewRevenueArrangement();

        // Step 5: Set up the new revenue arrangement with items
        this.setUpNewRevenueArrangement();
        Utils.resetRuntime(this.salesOrder);
    }

    removeArrangementsForWhReversal() {
        const { salesOrder } = this;
        const useWhDataForReversals =
            this.configProvider.useWhDataForReversalsConfig();

        // Remove the older arrangements from so here
        if (useWhDataForReversals) {
            if (salesOrder && salesOrder.orderType == "Modify") {
                salesOrder.revenueArrangement = [];
                salesOrder.revenueArrangementIds = [];
                if (
                    salesOrder?.professionalServiceDelivery
                        ?.soiProfessionalServiceDelivery?.length > 0
                ) {
                    salesOrder.professionalServiceDelivery.soiProfessionalServiceDelivery.forEach(
                        (item) => {
                            if (item.incrementalPurchases) {
                                item.incrementalPurchases = {};
                            }
                        }
                    );
                }
            }

            if (salesOrder && salesOrder.revenueArrangement) {
                salesOrder.revenueArrangement =
                    salesOrder.revenueArrangement.filter(
                        (ra) => ra.state == "Current"
                    );

                if (salesOrder.revenueArrangement.length > 1)
                    salesOrder.revenueArrangementIds = [
                        salesOrder.revenueArrangement[0].id
                    ];
            }
        }
    }

    init() {
        const { salesOrder } = this;

        if (!salesOrder.revenueArrangement) salesOrder.revenueArrangement = [];

        const revenueArrangements = salesOrder.revenueArrangement;

        salesOrder.newRevenueArrangement =
            revenueArrangements &&
            revenueArrangements.find(
                (revenueArrangement) => revenueArrangement.state === "New"
            );
        salesOrder.currentRevenueArrangement = revenueArrangements.find(
            (revenueArrangement) => revenueArrangement.state === "Current"
        );
    }

    createNewRevenueArrangement() {
        const { salesOrder } = this;

        if (
            isValidOrderType(salesOrder.orderType) &&
            !salesOrder.newRevenueArrangement
        ) {
            newRevenueArrangement(salesOrder);
        }
    }

    removeOpenPeriodRevenueArrangement() {
        const {
            salesOrder,
            organization,
            calendarConfig,
            errors,
            orgConfig,
            orgConfigByKey
        } = this;
        let removedRevenueArrangement;

        if (isValidOrderType(salesOrder.orderType)) {
            // Hold the removed revenueArrangement in the open period
            removedRevenueArrangement = removeRevenueArrangementsInOpenPeriod(
                salesOrder,
                organization,
                calendarConfig
            );
            if (
                removedRevenueArrangement &&
                salesOrder.modificationType == ModificationType.PROSPECTIVE
            ) {
                removedRevenueArrangement.revenueArrangementItem.forEach(
                    (item) => {
                        if (
                            item.plan &&
                            (item.recognitionRule.id ===
                                RecognitionRule.PROPORTIONAL_PERFORMANCE ||
                                item.recognitionRule.id ===
                                    RecognitionRule.PERCENT_OF_COMPLETE)
                        ) {
                            RevenueArrangementUtils.buildPSPlan(
                                item,
                                salesOrder,
                                removedRevenueArrangement,
                                errors,
                                calendarConfig,
                                orgConfig,
                                orgConfigByKey
                            );
                        }
                    }
                );
            }
        }
        this.context.setRemovedRevenueArrangement(removedRevenueArrangement);
    }

    setUpNewRevenueArrangement() {
        const {
            salesOrder,
            organization,
            calendarConfig,
            recognizeByTransactionDate
        } = this;

        let _period = (organization && organization.currentPeriod) || "200101";

        const orgPeriod = Period.toActgPeriod(_period, calendarConfig);
        let nextArrangementEffectiveDate = orgPeriod.endDate;

        if (salesOrder.newRevenueArrangement) {
            if (
                Period.isAfter(
                    salesOrder.orderDate,
                    nextArrangementEffectiveDate
                )
            ) {
                // order is in the future, revenue should also be in future
                nextArrangementEffectiveDate = salesOrder.orderDate;

                if (recognizeByTransactionDate) {
                    let _transactionDate = getMinTransactionDate(salesOrder);

                    if (
                        salesOrder.orderType !== "Modify" &&
                        _transactionDate &&
                        Period.isBefore(
                            nextArrangementEffectiveDate,
                            _transactionDate
                        )
                    ) {
                        nextArrangementEffectiveDate = _transactionDate;
                    } else if (salesOrder.orderType == "Modify") {
                        // recognize by transaction date is enabled, we will use first of the month to compare
                        // with the transaction date, if the max transaction date is higher than start of current system clock date
                        // then we will use start of month clock for new arrangement date.
                        const startOfCurrentMonth = Period.toActgPeriod(
                            _period,
                            calendarConfig
                        ).startDate;
                        const maxTransactionDate =
                            getMaxTransactionDate(salesOrder);

                        if (maxTransactionDate >= startOfCurrentMonth) {
                            // this should always be the end date of the month
                            // comparision would always be with the start of the month.
                            nextArrangementEffectiveDate = startOfCurrentMonth;
                        }
                    }
                }
            }

            if (
                salesOrder.revenueStartDate &&
                Period.isAfter(
                    salesOrder.revenueStartDate,
                    nextArrangementEffectiveDate
                )
            ) {
                // revenue start date is in the future
                nextArrangementEffectiveDate = salesOrder.revenueStartDate;
            }

            salesOrder.newRevenueArrangement.effectiveDate =
                nextArrangementEffectiveDate;
            salesOrder.newRevenueArrangement.postingDate =
                nextArrangementEffectiveDate;
        }

        // Add a rev arrangement item for all new salesOrderItems.
        salesOrder.newRevenueArrangement &&
            salesOrder.salesOrderItem.forEach((soItem) => {
                let raItem =
                    salesOrder.newRevenueArrangement.revenueArrangementItem.find(
                        (raItem) => raItem.salesOrderItemId === soItem.id
                    );
                if (!raItem) {
                    salesOrder.newRevenueArrangement.revenueArrangementItem.push(
                        RevenueArrangementUtils.newRevenueArrangementItem(
                            soItem,
                            salesOrder.newRevenueArrangement.id
                        )
                    );
                }
            });
    }
}

function isValidOrderType(orderType) {
    return (
        orderType === "New" ||
        orderType === "Modify" ||
        orderType === "Validation Error"
    );
}

function newRevenueArrangement(salesOrder) {
    const orderDate = Utils.formatDate(salesOrder.orderDate);

    let revenueStartDate =
        salesOrder.revenueStartDate != undefined
            ? Utils.formatDate(salesOrder.revenueStartDate)
            : orderDate;
    // setting revenueStartDate to salesOrder.revenueStartDate otherwaise defaulting it to orderDate

    let revenueArrangement = {};
    revenueArrangement.effectiveDate = revenueStartDate;
    revenueArrangement.isRevenueDelayed = salesOrder.isRevenueDelayed;
    revenueArrangement.salesOrderId = salesOrder.id;
    revenueArrangement.id = shortid.generate();
    revenueArrangement.new = true;
    revenueArrangement.revenueArrangementItem = salesOrder.salesOrderItem.map(
        (item, index) => {
            return RevenueArrangementUtils.newRevenueArrangementItem(
                item,
                revenueArrangement.id
            );
        }
    );
    revenueArrangement.state = "New";

    if (salesOrder.isTerminated) {
        revenueArrangement.isTerminated = salesOrder.isTerminated;
    }

    if (salesOrder.orderType == "Modify") {
        if (salesOrder.modificationType == ModificationType.PROSPECTIVE) {
            revenueArrangement.modificationType = ModificationType.PROSPECTIVE;
            revenueArrangement.modificationDate = salesOrder.modificationDate;
        } else {
            revenueArrangement.modificationType =
                ModificationType.RETROSPECTIVE;
        }
    }

    if (salesOrder.expenseItems && salesOrder.expenseItems.length) {
        revenueArrangement.expenseItems = Utils.cloneDeep(
            salesOrder.expenseItems
        );
    } else {
        revenueArrangement.expenseItems = [];
    }

    salesOrder.revenueArrangement.push(revenueArrangement);
    if (!salesOrder.revenueArrangementIds) {
        salesOrder.revenueArrangementIds = [];
    }
    salesOrder.revenueArrangementIds.push(revenueArrangement.id);
    revenueArrangement.jobId = salesOrder.jobId;
    salesOrder.newRevenueArrangement = revenueArrangement;
}

function removeRevenueArrangementsInOpenPeriod(
    salesOrder,
    organization,
    calendarConfig
) {
    // dont worry about this if there is no close period.
    if (!organization || !salesOrder.revenueArrangement) return;

    let { isRestatementMode } = organization;

    let currentPeriod = organization.currentPeriod;

    if (isRestatementMode) {
        currentPeriod = "200001";
    }

    const revenueArrangements = [];
    const revenueArrangementIds = [];
    const deletedRevenueArrangementIds = [];
    salesOrder.revenueArrangement.forEach((revenueArrangement) => {
        const raiEffectiveActgPeriod = Period.toActgPeriod(
            revenueArrangement.effectiveDate,
            calendarConfig
        );
        const effectivePeriod = raiEffectiveActgPeriod.period;
        // Leave all except for closed period
        if (
            effectivePeriod < currentPeriod ||
            revenueArrangement.state === "New"
        ) {
            revenueArrangements.push(revenueArrangement);
            revenueArrangementIds.push(revenueArrangement.id);
        } else {
            deletedRevenueArrangementIds.push(revenueArrangement.id);
            if (revenueArrangements.length > 0) {
                let previousRevenueArrangement =
                    revenueArrangements[revenueArrangements.length - 1];

                previousRevenueArrangement.state = "Current";
                delete previousRevenueArrangement.endDate;
            }
        }
    });

    let deletedRevenueArrangement;
    const currentRaId =
        salesOrder.currentRevenueArrangement &&
        salesOrder.currentRevenueArrangement.id;

    if (deletedRevenueArrangementIds.length > 0) {
        salesOrder.deletedRevenueArrangementIds = deletedRevenueArrangementIds;
        // Assuming we will have only one revenueArrangement in the open period
        deletedRevenueArrangement = salesOrder.revenueArrangement.find(
            (ra) => ra.id == deletedRevenueArrangementIds[0]
        );
        salesOrder.revenueArrangement = revenueArrangements;
        salesOrder.revenueArrangementIds = revenueArrangementIds;
    }

    salesOrder.historicalArrangements = {};
    for (let { id, effectiveDate } of revenueArrangements) {
        salesOrder.historicalArrangements[effectiveDate] = id;
    }

    if (deletedRevenueArrangementIds.includes(currentRaId)) {
        delete salesOrder.currentRevenueArrangement;
    }
    return deletedRevenueArrangement;
}

const getMaxTransactionDate = (salesOrder) => {
    let maxTransactionDate;

    salesOrder.salesOrderItem.forEach((item) => {
        if (!maxTransactionDate) {
            maxTransactionDate = item.transactionDate;
        } else if (item.transactionDate > maxTransactionDate) {
            maxTransactionDate = item.transactionDate;
        }
    });

    return maxTransactionDate;
};

const getMinTransactionDate = (salesOrder) => {
    let minTransactionDate = salesOrder.salesOrderItem[0].transactionDate;

    salesOrder.salesOrderItem.forEach((item) => {
        if (!minTransactionDate) {
            minTransactionDate = item.transactionDate;
        } else if (item.transactionDate < minTransactionDate) {
            minTransactionDate = item.transactionDate;
        }
    });

    return minTransactionDate;
};
